import React,{createContext,isValidElement,useContext,useMemo} from 'react';
import i18n from 'i18next';
import {useTranslation} from 'react-i18next';
import {observatoryUiFr as observatoryFr} from '../../shared/observatory-ui-fr';
import {publicLabel} from '../../shared/observatory-presentation';
import type {ObservatoryRelease} from '../../shared/observatory';

const NamesContext=createContext<Set<string>>(new Set(['Artificial Careers','AI Careers','Kesha','ChatGPT','OpenAI','Codex','Claude','Anthropic','Claude Code','Cursor','Gemini','Google','Mistral','GitHub Copilot','VS Code']));
export const observatoryUiLocale=()=>String(i18n.language||i18n.resolvedLanguage||'en').toLowerCase().startsWith('fr')?'fr-FR':'en-US';
export const observatoryText=(value:string)=>observatoryUiLocale()==='fr-FR'?observatoryFr(value):value;
export const observatoryPublicLabel=(value:string)=>/^[A-Z]{2}$/.test(value)?new Intl.DisplayNames([observatoryUiLocale()],{type:'region'}).of(value)||value:observatoryUiLocale()==='fr-FR'?observatoryFr(publicLabel(value)):publicLabel(value);

export function useObservatoryLocale(){
  const {i18n:instance}=useTranslation();
  const french=String(instance.language||instance.resolvedLanguage||'en').toLowerCase().startsWith('fr');
  return {french,locale:french?'fr-FR':'en-US',text:(value:string)=>french?observatoryFr(value):value};
}

export function ObservatoryLocaleProvider({release,children}:{release:ObservatoryRelease|null;children:React.ReactNode}){
  const {french}=useObservatoryLocale();
  const names=useMemo(()=>{
    const result=new Set(['Artificial Careers','AI Careers','Kesha','ChatGPT','OpenAI','Codex','Claude','Anthropic','Claude Code','Cursor','Gemini','Google','Mistral','GitHub Copilot','VS Code']);
    if(!french||!release)return result;
    for(const tool of [...(release.taxonomy||[]),...(release.productTaxonomy||[])])if(tool.id!=='cli'){result.add(tool.name);if(tool.vendor)result.add(tool.vendor);}
    const references=[release.francePayReference,release.countryPayReference,...Object.values(release.countryPayReferences||{})];
    for(const reference of references)for(const rows of [reference?.employerCoverage,reference?.employers,reference?.ecosystemEmployers])for(const row of rows||[]){if(row.company)result.add(row.company);if(row.domain)result.add(row.domain);}
    return result;
  },[release,french]);
  return <NamesContext.Provider value={names}>{children}</NamesContext.Provider>;
}

function translateTree(node:React.ReactNode,text:(value:string)=>string):React.ReactNode{
  if(typeof node==='string')return text(node);
  if(typeof node==='number')return node.toLocaleString('fr-FR');
  if(Array.isArray(node))return React.Children.map(node,child=>translateTree(child,text));
  if(!isValidElement(node))return node;
  const element=node as React.ReactElement<Record<string,unknown>>;
  if(element.props.translate==='no'||element.type==='script'||element.type==='style'||element.type===ObservatoryLocalized)return element;
  const props:Record<string,unknown>={};
  for(const name of ['aria-label','aria-valuetext','alt','title','placeholder'])if(typeof element.props[name]==='string')props[name]=text(element.props[name] as string);
  if(element.props.children!==undefined)props.children=translateTree(element.props.children as React.ReactNode,text);
  return React.cloneElement(element,props);
}

export function ObservatoryLocalized({children}:{children:React.ReactNode}){
  const {french}=useObservatoryLocale();const names=useContext(NamesContext);
  const translatedTree=useMemo(()=>{
    if(!french)return children;
    const cache=new Map<string,string>();const list=[...names];
    const text=(value:string)=>{if(names.has(value.trim()))return value;const cached=cache.get(value);if(cached!==undefined)return cached;const translated=observatoryFr(value,list.filter(name=>value.includes(name)));cache.set(value,translated);return translated;};
    return translateTree(children,text);
  },[children,french,names]);
  return <>{translatedTree}</>;
}
